/* eslint-disable react/prop-types */
import React from 'react';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { TextField } from '@mui/material';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  container: (base) => ({ ...base, minWidth: 190 }),
  control: (base) => ({ ...base, minHeight: 32 })
};

const ReferenceFormatter = ({ value }) => <span>{value?.name || '-'}</span>;

const ReferenceEditor = ({ value, row, onValueChange, onBlur, autoFocus, disabled, options, optionFilter }) => {
  const selectOptions = options
    .filter((option) => !optionFilter || optionFilter(option, row))
    .map((option) => ({
      value: String(option.value),
      label: option.name,
      reference: option
    }));
  const selected = selectOptions.find((option) => option.value === String(value?.value || '')) || null;

  return (
    <ReactSelect
      autoFocus={autoFocus}
      onBlur={onBlur}
      isDisabled={disabled}
      isSearchable
      value={selected}
      options={selectOptions}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={selectStyles}
      onChange={(option) => onValueChange(option?.reference || null)}
    />
  );
};

export const ReferenceProvider = ({ options, optionFilter, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={ReferenceFormatter}
    editorComponent={(editorProps) => (
      <ReferenceEditor {...editorProps} options={options} optionFilter={optionFilter} />
    )}
  />
);

const BoxshallProductEditor = ({ value, onValueChange, onBlur, autoFocus, disabled, loadOptions }) => {
  const selected = value?.id
    ? {
        value: String(value.id),
        label: value.name,
        reference: value
      }
    : null;

  return (
    <AsyncSelect
      autoFocus={autoFocus}
      onBlur={onBlur}
      isDisabled={disabled}
      isSearchable
      cacheOptions
      defaultOptions
      value={selected}
      loadOptions={loadOptions}
      placeholder="Search Boxshall Product"
      noOptionsMessage={({ inputValue }) => (inputValue ? 'No Boxshall Product found' : 'Type to search products')}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={selectStyles}
      onChange={(option) => onValueChange(option?.reference || null)}
    />
  );
};

export const BoxshallProductProvider = ({ loadOptions, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={ReferenceFormatter}
    editorComponent={(editorProps) => <BoxshallProductEditor {...editorProps} loadOptions={loadOptions} />}
  />
);

const dateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const DateFormatter = ({ value }) => {
  const normalized = dateValue(value);
  if (!normalized) return <span>-</span>;
  const [year, month, day] = normalized.split('-');
  return <span>{`${day}/${month}/${year}`}</span>;
};

const DateEditor = ({ value, onValueChange, onBlur, autoFocus, disabled }) => (
  <TextField
    autoFocus={autoFocus}
    onBlur={onBlur}
    disabled={disabled}
    type="date"
    size="small"
    variant="standard"
    value={dateValue(value)}
    onChange={(event) => onValueChange(event.target.value)}
    fullWidth
  />
);

export const DateProvider = (props) => (
  <DataTypeProvider {...props} formatterComponent={DateFormatter} editorComponent={DateEditor} />
);

const NumberFormatter = ({ value, currency }) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return <span>-</span>;
  if (!currency) return <span>{value}</span>;
  return (
    <span>
      {/* eslint-disable-next-line no-undef */}
      {new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      }).format(Number(value))}
    </span>
  );
};

const NumberEditor = ({ value, onValueChange, onBlur, autoFocus, disabled }) => (
  <TextField
    autoFocus={autoFocus}
    onBlur={onBlur}
    disabled={disabled}
    type="number"
    size="small"
    variant="standard"
    value={value ?? ''}
    inputProps={{ step: 'any' }}
    onChange={(event) => onValueChange(event.target.value)}
    fullWidth
  />
);

export const NumberProvider = ({ currency = false, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={(formatterProps) => <NumberFormatter {...formatterProps} currency={currency} />}
    editorComponent={NumberEditor}
  />
);
